import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
    getAllClients, deleteClient, updateClient, getAllConsultants,
    getBuilderStatusBatch, sendClientPasswordReset, BuilderStatus,
} from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { getValidxBridgeUrl } from '../../utils/validxBridge';
import {
    Users, UserCheck, UserX, UserPlus, Search,
    ChevronRight, ChevronUp, ChevronDown, Plus, ExternalLink,
    Loader2, ArrowUpDown, ChevronsLeft, ChevronsRight, ChevronLeft,
    Trash2, CheckSquare, Pencil, User, Mail, Phone, Globe, UserCog,
    KeyRound, Save, X,
} from 'lucide-react';

const BUILDER_FALLBACK = ((process.env.REACT_APP_BUILDER_URL as string) || 'https://builder.agentsanywhere.ai').replace(/\/+$/, '');

interface Client {
    id: string;
    name: string;
    email: string;
    phone?: string;
    website_url?: string;
    status?: string;
    created_at: string;
    config?: any;
    consultant_id?: string;
    consultant_name?: string;
    provider_name?: string | null;
}

const ConsultantDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userRole] = useState(localStorage.getItem('user_role') || 'client');
    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
    const [consultants, setConsultants] = useState<{ id: string; name: string }[]>([]);
    const [builderStatuses, setBuilderStatuses] = useState<Record<string, BuilderStatus>>({});
    const [builderAppUrl, setBuilderAppUrl] = useState<string | null>(null);
    const [reassigningIds, setReassigningIds] = useState<Set<string>>(new Set());

    // Open the client's builder workspace through the SIGNED auth bridge — a
    // raw builder URL has no session (and /workspace/<id> is not even a
    // builder route → 404). Lands on /customer/workspace: the Open Workspace
    // home (2026-09-21: this used to say /customer/agents, the "new agent +
    // agents list" page retired when Open Workspace shipped).
    const openBuilderWorkspace = async (clientId: string) => {
        // Open the tab SYNCHRONOUSLY (inside the click gesture) — window.open
        // after an await gets popup-blocked, which reads as a dead click.
        const tab = window.open('about:blank', '_blank');
        const base = (builderAppUrl || BUILDER_FALLBACK).replace(/\/+$/, '');
        try {
            const bridgeUrl = await getValidxBridgeUrl(base, '/customer/workspace', {
                workspaceId: clientId,
            });
            if (tab) tab.location.href = bridgeUrl;
            else window.location.href = bridgeUrl;
        } catch (e) {
            tab?.close();
            toast.error('Could not open the workspace — try again.');
        }
    };

    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentClient, setCurrentClient] = useState<Client | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<Client>>({});
    const [saving, setSaving] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);

    useEffect(() => {
        fetchClients();
        if (userRole === 'super') {
            getAllConsultants()
                .then(res => setConsultants(res?.consultants || []))
                .catch(err => console.error('Error fetching consultants:', err));
        }
    }, []);

    // Builder (workspace) provisioning status per client — powers the row link.
    useEffect(() => {
        if (clients.length === 0) return;
        getBuilderStatusBatch(clients.map(c => c.id))
            .then(res => {
                setBuilderStatuses(res.statuses);
                setBuilderAppUrl(res.builderAppUrl);
            })
            .catch(err => console.warn('[ConsultantDashboard] Builder status lookup failed:', err));
    }, [clients]);

    const fetchClients = async () => {
        try {
            const data = await getAllClients();
            setClients(data);
        } catch (err) {
            console.error('Error fetching clients:', err);
            toast.error('Failed to fetch clients');
            setError('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    // Inline consultant reassignment straight from the listing row (super only).
    const handleRowReassign = async (client: Client, consultantId: string) => {
        if (!consultantId || consultantId === client.consultant_id) return;
        setReassigningIds(prev => new Set(prev).add(client.id));
        try {
            await updateClient(client.id, { consultant_id: consultantId });
            const consultantName = consultants.find(c => c.id === consultantId)?.name || 'consultant';
            toast.success(`${client.name} assigned to ${consultantName}`);
            fetchClients();
        } catch (err) {
            console.error('Error reassigning consultant:', err);
            toast.error('Failed to reassign consultant');
        } finally {
            setReassigningIds(prev => {
                const next = new Set(prev);
                next.delete(client.id);
                return next;
            });
        }
    };

    const openEditModal = (client: Client) => {
        setCurrentClient(client);
        setEditFormData({
            name: client.name,
            email: client.email,
            phone: client.phone || '',
            website_url: client.website_url || '',
            consultant_id: userRole === 'super'
                ? (client.consultant_id || '')
                : (localStorage.getItem('user_id') || ''),
        });
        setShowEditModal(true);
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveEdit = async () => {
        if (!currentClient) return;
        try {
            setSaving(true);
            // Empty consultant_id would trip the FK constraint — omit it.
            const updateData = { ...editFormData };
            if (updateData.consultant_id === '') delete updateData.consultant_id;
            await updateClient(currentClient.id, updateData);
            toast.success('Client updated successfully');
            setShowEditModal(false);
            fetchClients();
        } catch (err) {
            console.error('Error updating client:', err);
            toast.error('Failed to update client');
        } finally {
            setSaving(false);
        }
    };

    const handleSendReset = async (client: Client) => {
        try {
            setSendingReset(true);
            const res = await sendClientPasswordReset(client.id);
            toast.success(res?.message || `Password reset link sent to ${client.email}`);
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || err.message || 'Failed to send reset link');
        } finally {
            setSendingReset(false);
        }
    };

    // Filter clients based on status
    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'active') return !client.status || client.status === 'active';
            return client.status === statusFilter;
        });
    }, [clients, statusFilter]);

    // Search, sort, paginate
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<string>('name');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [pageIndex, setPageIndex] = useState(0);
    const pageSize = 15;

    // Bulk selection
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const toggleSelectAll = () => {
        if (selectedIds.size === paginated.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(paginated.map(c => c.id)));
        }
    };

    const selectAllVisible = () => {
        setSelectedIds(new Set(sorted.map(c => c.id)));
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        setShowDeleteConfirm(false);
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setDeleting(true);
        let deleted = 0;
        let failed = 0;
        for (const id of Array.from(selectedIds)) {
            try {
                await deleteClient(id);
                deleted++;
            } catch {
                failed++;
            }
        }
        setDeleting(false);
        setShowDeleteConfirm(false);
        setSelectedIds(new Set());
        if (deleted > 0) toast.success(`Permanently deleted ${deleted} client${deleted !== 1 ? 's' : ''}`);
        if (failed > 0) toast.error(`Failed to delete ${failed} client${failed !== 1 ? 's' : ''}`);
        fetchClients();
    };

    const searched = useMemo(() => {
        if (!searchQuery.trim()) return filteredClients;
        const q = searchQuery.toLowerCase();
        return filteredClients.filter(c =>
            c.name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.website_url?.toLowerCase().includes(q) ||
            c.consultant_name?.toLowerCase().includes(q)
        );
    }, [filteredClients, searchQuery]);

    const sorted = useMemo(() => {
        return [...searched].sort((a: any, b: any) => {
            const av = (a[sortField] || '').toString().toLowerCase();
            const bv = (b[sortField] || '').toString().toLowerCase();
            const cmp = av.localeCompare(bv);
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [searched, sortField, sortDir]);

    const pageCount = Math.ceil(sorted.length / pageSize);
    const paginated = sorted.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    // Reset to page 0 when filters change
    useEffect(() => { setPageIndex(0); }, [statusFilter, searchQuery]);

    const toggleSort = (field: string) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-gray-300 ml-1" />;
        return sortDir === 'asc'
            ? <ChevronUp className="w-3 h-3 text-brand-500 ml-1" />
            : <ChevronDown className="w-3 h-3 text-brand-500 ml-1" />;
    };

    // Stats
    const activeCount = clients.filter(c => !c.status || c.status === 'active').length;
    const inactiveCount = clients.filter(c => c.status === 'inactive').length;
    const newThisMonth = clients.filter(c => {
        const d = new Date(c.created_at);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading clients...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="text-red-500 text-center p-4">{error}</div>;
    }

    return (
        <div>
            {/* Page header */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage and monitor your client accounts</p>
                </div>
                <button
                    onClick={() => navigate('/consultant/clients/new')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-all shadow-sm hover:shadow-md whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    New Client
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                    { label: 'Total Clients', value: clients.length, Icon: Users, color: 'text-brand-500', bg: 'bg-brand-50' },
                    { label: 'Active', value: activeCount, Icon: UserCheck, color: 'text-brand-500', bg: 'bg-brand-50' },
                    { label: 'Inactive', value: inactiveCount, Icon: UserX, color: 'text-gray-500', bg: 'bg-gray-50' },
                    { label: 'New This Month', value: newThisMonth, Icon: UserPlus, color: 'text-gray-700', bg: 'bg-gray-100' },
                ].map(({ label, value, Icon, color, bg }) => (
                    <div key={label} className="bg-white rounded-xl border border-gray-100 px-4 py-4 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
                            <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
                            <div className="text-xs text-gray-500 truncate">{label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
                <div className="flex items-center justify-between mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-3">
                        <CheckSquare className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-700">
                            {selectedIds.size} client{selectedIds.size !== 1 ? 's' : ''} selected
                        </span>
                        {selectedIds.size < sorted.length && (
                            <button onClick={selectAllVisible} className="text-sm text-red-600 hover:text-red-700 underline">
                                Select all {sorted.length}
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={clearSelection} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-white rounded-lg transition-colors">
                            Cancel
                        </button>
                        {!showDeleteConfirm ? (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete Selected
                            </button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-red-700 font-medium">Permanently delete?</span>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={deleting}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                                >
                                    {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                    {deleting ? 'Deleting...' : 'Yes, Delete All'}
                                </button>
                                <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-white rounded-lg transition-colors">
                                    No
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Search + Filter bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <div className="relative min-w-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search by name, email, website..."
                            className="pl-9 pr-4 py-2 w-72 max-w-full border border-gray-200 rounded-lg text-sm bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all"
                        />
                    </div>
                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden text-sm">
                        {(['active', 'inactive', 'all'] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => setStatusFilter(f)}
                                className={`px-3 py-2 font-medium transition-colors ${
                                    statusFilter === f
                                        ? 'bg-brand-50 text-brand-600'
                                        : 'text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <span className="text-sm text-gray-400">
                    {sorted.length} client{sorted.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <div className={`min-w-[780px] ${userRole === 'super' ? 'min-w-[980px]' : ''}`}>
                        {/* Header */}
                        <div className={`grid ${userRole === 'super' ? 'grid-cols-[40px_2fr_0.8fr_1.1fr_0.9fr_1.2fr_0.7fr_100px_32px]' : 'grid-cols-[40px_2fr_0.8fr_1.2fr_0.7fr_100px_32px]'} px-5 py-3 border-b border-gray-100`}>
                            <div className="flex items-center justify-center">
                                <input
                                    type="checkbox"
                                    checked={paginated.length > 0 && paginated.every(c => selectedIds.has(c.id))}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20 cursor-pointer"
                                />
                            </div>
                            <button onClick={() => toggleSort('name')} className="flex items-center text-[11px] font-medium text-gray-400 uppercase tracking-wider hover:text-gray-600">
                                Client <SortIcon field="name" />
                            </button>
                            <button onClick={() => toggleSort('status')} className="flex items-center text-[11px] font-medium text-gray-400 uppercase tracking-wider hover:text-gray-600">
                                Status <SortIcon field="status" />
                            </button>
                            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Website</span>
                            {userRole === 'super' && <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Provider</span>}
                            {userRole === 'super' && (
                                <button onClick={() => toggleSort('consultant_name')} className="flex items-center text-[11px] font-medium text-gray-400 uppercase tracking-wider hover:text-gray-600">
                                    Consultant <SortIcon field="consultant_name" />
                                </button>
                            )}
                            <button onClick={() => toggleSort('created_at')} className="flex items-center text-[11px] font-medium text-gray-400 uppercase tracking-wider hover:text-gray-600">
                                Created <SortIcon field="created_at" />
                            </button>
                            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider text-center">Actions</span>
                            <span />
                        </div>

                        {/* Rows */}
                        {paginated.length === 0 ? (
                            <div className="px-5 py-12 text-center text-sm text-gray-400">
                                No {statusFilter === 'all' ? '' : statusFilter + ' '}clients found.
                            </div>
                        ) : paginated.map(client => {
                            const isClientActive = !client.status || client.status === 'active';
                            const websiteShort = client.website_url?.replace(/^https?:\/\//, '').replace(/\/$/, '') || '';
                            return (
                                <div
                                    key={client.id}
                                    onClick={() => navigate(`/consultant/clients/${client.id}`)}
                                    className={`grid ${userRole === 'super' ? 'grid-cols-[40px_2fr_0.8fr_1.1fr_0.9fr_1.2fr_0.7fr_100px_32px]' : 'grid-cols-[40px_2fr_0.8fr_1.2fr_0.7fr_100px_32px]'} items-center px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/70 cursor-pointer transition-colors group`}
                                >
                                    {/* Checkbox */}
                                    <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(client.id)}
                                            onChange={() => {
                                                setSelectedIds(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(client.id)) next.delete(client.id); else next.add(client.id);
                                                    return next;
                                                });
                                            }}
                                            className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20 cursor-pointer"
                                        />
                                    </div>
                                    {/* Client */}
                                    <div className="flex items-center gap-3 min-w-0 pr-4">
                                        <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 text-xs font-semibold shrink-0">
                                            {client.name?.charAt(0).toUpperCase() || '?'}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">{client.name}</p>
                                            <p className="text-xs text-gray-400 truncate">{client.email}</p>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <span
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${
                                                isClientActive
                                                    ? 'bg-brand-50 text-brand-700 ring-brand-200'
                                                    : 'bg-gray-50 text-gray-500 ring-gray-200'
                                            }`}
                                        >
                                            <span className={`w-1.5 h-1.5 rounded-full ${isClientActive ? 'bg-brand-500' : 'bg-gray-400'}`} />
                                            {client.status || 'active'}
                                        </span>
                                    </div>

                                    {/* Website */}
                                    <div className="min-w-0">
                                        {websiteShort ? (
                                            <span className="inline-flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]">
                                                <span className="truncate">{websiteShort}</span>
                                                <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </span>
                                        ) : (
                                            <span className="text-sm text-gray-400">—</span>
                                        )}
                                    </div>

                                    {userRole === 'super' && (
                                        <div className="min-w-0 truncate pr-3 text-sm text-gray-600">{client.provider_name || '—'}</div>
                                    )}

                                    {/* Consultant (super only) — inline reassign, saves on change */}
                                    {userRole === 'super' && (
                                        <div className="pr-3" onClick={e => e.stopPropagation()}>
                                            <div className="relative inline-flex items-center w-full max-w-[180px]">
                                                <select
                                                    value={client.consultant_id || ''}
                                                    onChange={(e) => handleRowReassign(client, e.target.value)}
                                                    disabled={reassigningIds.has(client.id)}
                                                    title="Assign consultant"
                                                    className={`w-full text-sm border rounded-lg pl-2.5 pr-7 py-1.5 cursor-pointer focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 outline-none appearance-none transition-colors disabled:opacity-50 truncate ${
                                                        client.consultant_id
                                                            ? 'border-gray-200 text-gray-700 bg-white'
                                                            : 'border-amber-300 text-amber-700 bg-amber-50'
                                                    }`}
                                                >
                                                    <option value="" disabled>Not Assigned</option>
                                                    {consultants.map(c => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </select>
                                                {reassigningIds.has(client.id) ? (
                                                    <Loader2 className="w-3.5 h-3.5 absolute right-2 pointer-events-none animate-spin text-gray-400" />
                                                ) : (
                                                    <ChevronDown className="w-3.5 h-3.5 absolute right-2 pointer-events-none text-gray-400" />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Created */}
                                    <div>
                                        <span className="text-sm text-gray-500">{new Date(client.created_at).toLocaleDateString()}</span>
                                    </div>

                                    {/* Actions: open workspace + edit */}
                                    <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                                        {/* Always open-able: the workspace is reached via the SIGNED
                                            EMAIL bridge (request-agent), the same path impersonation and
                                            per-project clicks use — it resolves by founder email, not by
                                            a builder workspace-id match. The provisioning status probe is
                                            only an id-keyed lookup and must NOT gate access: when it can't
                                            find the workspace by id it returns "not provisioned", which
                                            would falsely disable a button whose action works fine. Show the
                                            project count when the probe happens to have it, never block. */}
                                        {(() => {
                                            const pc = builderStatuses[client.id]?.projectCount;
                                            const title = typeof pc === 'number'
                                                ? `Open ${client.name}'s builder workspace (${pc} project${pc === 1 ? '' : 's'})`
                                                : `Open ${client.name}'s builder workspace`;
                                            return (
                                                <button
                                                    onClick={() => { void openBuilderWorkspace(client.id); }}
                                                    title={title}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </button>
                                            );
                                        })()}
                                        <button
                                            onClick={() => openEditModal(client)}
                                            title="Edit client"
                                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Chevron */}
                                    <div className="flex items-center justify-center">
                                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pagination */}
                {pageCount > 1 && (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPageIndex(0)} disabled={pageIndex === 0} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button onClick={() => setPageIndex(Math.max(0, pageIndex - 1))} disabled={pageIndex === 0} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="px-3 text-sm text-gray-600">
                                Page <strong>{pageIndex + 1}</strong> of <strong>{pageCount}</strong>
                            </span>
                            <button onClick={() => setPageIndex(Math.min(pageCount - 1, pageIndex + 1))} disabled={pageIndex >= pageCount - 1} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button onClick={() => setPageIndex(pageCount - 1)} disabled={pageIndex >= pageCount - 1} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>
                        <span className="text-xs text-gray-400">{sorted.length} total</span>
                    </div>
                )}
            </div>

            {/* Edit Client Modal — portal to <body> with z above the sidebar
                (sidebar is z-[100]) so the overlay covers the whole app. */}
            {showEditModal && currentClient && createPortal(
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center z-[110] p-4"
                    onClick={() => setShowEditModal(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-black text-white flex items-center justify-center text-sm font-semibold uppercase">
                                    {(currentClient.name || '?').charAt(0)}
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-gray-900">Edit Client</h2>
                                    <p className="text-xs text-gray-500">{currentClient.name}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {builderStatuses[currentClient.id]?.provisioned && (
                                    <button
                                        onClick={() => { void openBuilderWorkspace(currentClient.id); }}
                                        title="Open this client's builder workspace"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        Workspace
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Full Name</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            name="name"
                                            value={editFormData.name || ''}
                                            onChange={handleEditInputChange}
                                            placeholder="Client name"
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={editFormData.email || ''}
                                            onChange={handleEditInputChange}
                                            placeholder="email@company.com"
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone Number</label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            name="phone"
                                            value={editFormData.phone || ''}
                                            onChange={handleEditInputChange}
                                            placeholder="+1 (555) 000-0000"
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Website</label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            name="website_url"
                                            value={editFormData.website_url || ''}
                                            onChange={handleEditInputChange}
                                            placeholder="https://example.com"
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {userRole === 'super' && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Assigned Consultant</label>
                                    <div className="relative">
                                        <UserCog className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <select
                                            name="consultant_id"
                                            value={editFormData.consultant_id || ''}
                                            onChange={handleEditInputChange}
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all outline-none appearance-none"
                                        >
                                            <option value="">-- Select Consultant --</option>
                                            {consultants.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => handleSendReset(currentClient)}
                                disabled={sendingReset}
                                title="Email a password-reset link to the client's login address"
                                className="inline-flex items-center gap-2 px-3.5 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                                {sendingReset ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                                Send reset link
                            </button>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-5 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 shadow-sm whitespace-nowrap"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ConsultantDashboard;
